import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import Cookies from 'js-cookie'

// Mock API call for frontend development
const mockLoginApi = (credentials) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      // Mock successful login for any email/password combination
      if (credentials.email && credentials.password) {
        const userRoles = {
          'admin@serviceerp.com': ['SUPER_ADMIN', 'DASHBOARD_READ', 'CRM_READ', 'CRM_LEADS_READ', 'CRM_OPPORTUNITIES_READ', 'CRM_QUOTES_READ', 'CRM_CUSTOMERS_READ', 'SALES_READ', 'SALES_ORDERS_READ', 'SALES_INVOICES_READ', 'SALES_RECEIPTS_READ', 'CONTRACTS_READ', 'CONTRACTS_TEMPLATES_READ', 'CONTRACTS_OBLIGATIONS_READ', 'PROJECTS_READ', 'PROJECTS_TASKS_READ', 'PROJECTS_MILESTONES_READ', 'PROJECTS_CR_READ', 'TIMESHEETS_READ', 'TIMESHEETS_APPROVE', 'TIMESHEETS_REPORTS_READ', 'EXPENSES_READ', 'EXPENSES_APPROVE', 'EXPENSES_REPORTS_READ', 'PROCUREMENT_READ', 'PROCUREMENT_PR_READ', 'PROCUREMENT_PO_READ', 'PROCUREMENT_VENDORS_READ', 'PROCUREMENT_GRN_READ', 'SUBSCRIPTIONS_READ', 'SUBSCRIPTIONS_RENEWALS_READ', 'SUBSCRIPTIONS_REPORTS_READ', 'ASSETS_READ', 'ASSETS_ASSIGNMENTS_READ', 'ASSETS_MAINTENANCE_READ', 'REPORTS_READ', 'REPORTS_FINANCIAL_READ', 'REPORTS_PROJECTS_READ', 'REPORTS_UTILIZATION_READ', 'REPORTS_CUSTOM_READ', 'CALENDAR_READ', 'COMPANIES_READ', 'SETTINGS_READ', 'SETTINGS_USER_READ', 'SETTINGS_COMPANY_READ', 'SETTINGS_SYSTEM_READ', 'HELP_READ'],
          'manager@serviceerp.com': ['MANAGER', 'DASHBOARD_READ', 'CRM_READ', 'CRM_LEADS_READ', 'CRM_OPPORTUNITIES_READ', 'CRM_QUOTES_READ', 'SALES_READ', 'PROJECTS_READ', 'TIMESHEETS_READ', 'TIMESHEETS_APPROVE', 'EXPENSES_READ', 'EXPENSES_APPROVE', 'PROCUREMENT_READ', 'REPORTS_READ'],
          'user@serviceerp.com': ['USER', 'DASHBOARD_READ', 'CRM_LEADS_READ', 'PROJECTS_READ', 'TIMESHEETS_READ', 'EXPENSES_READ'],
        };

        const user = {
          id: '123',
          name: 'John Doe',
          email: credentials.email,
          companies: [
            { companyId: 'comp1', role: 'ADMIN', permissions: userRoles[credentials.email] || ['USER'] },
            { companyId: 'comp2', role: 'USER', permissions: ['USER', 'DASHBOARD_READ'] },
          ],
        };

        const companies = [
          { id: 'comp1', name: 'Acme Corp India', type: 'Pvt Ltd', gstn: 'GSTIN123' },
          { id: 'comp2', name: 'Acme Corp US', type: 'LLC', gstn: 'GSTIN456' },
        ];

        resolve({ 
          user, 
          token: 'mock-jwt-token-' + Date.now(),
          refreshToken: 'mock-refresh-token-' + Date.now(),
          permissions: userRoles[credentials.email] || ['USER']
        });
      } else {
        reject(new Error('Email and password are required'));
      }
    }, 1000); // Simulate network delay
  });
};

// Async thunks for API calls
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const data = await mockLoginApi(credentials);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
)

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      // Mock logout - just clear local data
      // This will be replaced with actual API call when backend is ready
      
      // Clear cookies
      Cookies.remove('token')
      Cookies.remove('refreshToken')
      
      return true
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = Cookies.get('refreshToken')
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }
      
      // Mock token refresh - just return new tokens
      // This will be replaced with actual API call when backend is ready
      return {
        token: 'mock-jwt-token-' + Date.now(),
        refreshToken: 'mock-refresh-token-' + Date.now()
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  user: null,
  token: Cookies.get('token') || null,
  refreshToken: Cookies.get('refreshToken') || null,
  isAuthenticated: !!Cookies.get('token'),
  isLoading: false,
  error: null,
  permissions: [],
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload }
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
        state.isAuthenticated = true
        state.permissions = action.payload.permissions || []
        
        // Set cookies
        Cookies.set('token', action.payload.token, { expires: 7 })
        Cookies.set('refreshToken', action.payload.refreshToken, { expires: 30 })
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
        state.isAuthenticated = false
      })
      
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false
        state.user = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.permissions = []
        state.error = null
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
        
        // Update cookies
        Cookies.set('token', action.payload.token, { expires: 7 })
        Cookies.set('refreshToken', action.payload.refreshToken, { expires: 30 })
      })
      .addCase(refreshToken.rejected, (state) => {
        // If refresh fails, logout user
        state.user = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.permissions = []
        
        // Clear cookies
        Cookies.remove('token')
        Cookies.remove('refreshToken')
      })
  },
})

export const { clearError, updateUser, setPermissions } = authSlice.actions
export default authSlice.reducer

